export function getTimeAgo(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  if (diffMins < 60) return `${diffMins} minutes ago`;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 24) return `${diffHours} hours ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} days ago`;
}

export function parseDateOnly(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function startOfToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

interface EventRange {
  rangeStart: Date;
  rangeEnd?: Date;
}

/**
 * Resolve the date range for the events list's month filter.
 * 'all' -> open-ended from today; 'this-month' / 'YYYY-MM' -> that month,
 * clamped so the range never starts before today.
 */
export function getEventFilterRange(selectedMonth: string, today = startOfToday()): EventRange {
  const normalizedToday = new Date(today);
  normalizedToday.setHours(0, 0, 0, 0);

  if (selectedMonth === 'this-month') {
    const monthStart = new Date(normalizedToday.getFullYear(), normalizedToday.getMonth(), 1);
    const monthEnd = new Date(normalizedToday.getFullYear(), normalizedToday.getMonth() + 1, 0);
    return {
      rangeStart: monthStart < normalizedToday ? normalizedToday : monthStart,
      rangeEnd: monthEnd,
    };
  }

  if (selectedMonth !== 'all') {
    const [year, month] = selectedMonth.split('-').map(Number);
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0);
    return {
      rangeStart: monthStart < normalizedToday ? normalizedToday : monthStart,
      rangeEnd: monthEnd,
    };
  }

  return { rangeStart: normalizedToday };
}

/**
 * Converts a stored 24-hour "HH:MM" time to "H:MM AM/PM".
 *
 * Kept in sync with the CMS-side twin in `cms/server/src/utils/format-time.ts`
 * (Strapi cannot import Next.js code, so the implementation is deliberately duplicated).
 * Change both, or neither.
 *
 * timeStart/timeEnd are plain Strapi strings with no enforced format and two shapes are live in
 * the data: TimePicker writes 24H ("17:30"), while seeded rows hold 12H ("6:00 PM"). Anchoring the
 * match to the whole string is what protects the latter — a prefix match would read "6:00 PM" as
 * hour 6 and render it "6:00 AM", wrong by twelve hours rather than merely unformatted. Values with
 * a trailing meridiem or out-of-range parts therefore fall through and are returned untouched.
 * Surrounding whitespace is tolerated.
 */
export function formatTime12H(time: string | null | undefined): string {
  if (!time) return '';
  const match = time.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return time;
  const h24 = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (h24 > 23 || minutes > 59) return time;
  const period = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return `${h12}:${match[2]} ${period}`;
}

